import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  normalizeDesignName,
  buildDesignIndexDocId,
  getDesignTokens,
  scoreDesignNameSimilarity
} from './designNameMatching';
import { getMockupUrl, getPersistableMockupUrl } from './fileHelpers';
import { buildLineSnapshotFromLine } from './customerDesignLibrary';

export const CUSTOMER_DESIGN_MOCKUPS_COLLECTION = 'customerDesignMockups';

const DEFAULT_MIN_SCORE = 0.5;
const DEFAULT_LIMIT = 2;

function scoreEntryAgainstDesignName(designName, entry) {
  const candidates = [
    entry.displayName,
    entry.normalizedKey?.replace(/_/g, ' ')
  ].filter(Boolean);

  if (!candidates.length) return 0;

  let score = Math.max(
    ...candidates.map((candidate) => scoreDesignNameSimilarity(designName, candidate))
  );

  const typedTokens = getDesignTokens(designName);
  if (typedTokens.length >= 2) {
    for (const candidate of candidates) {
      const entryTokens = new Set(getDesignTokens(candidate));
      if (typedTokens.every((token) => entryTokens.has(token))) {
        score = Math.max(score, 0.75);
      }
    }
  }

  return score;
}

/**
 * Find mockup suggestions for a design name, scoped to one customer.
 * @param {string} customerId
 * @param {string} designName
 * @param {{ minScore?: number, limit?: number }} [options]
 * @returns {Promise<Array>}
 */
export async function findSuggestedMockups(customerId, designName, options = {}) {
  if (!customerId || !designName?.trim()) return [];

  const minScore = options.minScore ?? DEFAULT_MIN_SCORE;
  const limit = options.limit ?? DEFAULT_LIMIT;
  const trimmedName = designName.trim();

  const snap = await getDocs(
    query(
      collection(db, CUSTOMER_DESIGN_MOCKUPS_COLLECTION),
      where('customerId', '==', customerId)
    )
  );

  const scored = snap.docs
    .map((entryDoc) => ({ id: entryDoc.id, ...entryDoc.data() }))
    .map((entry) => ({
      ...entry,
      score: scoreEntryAgainstDesignName(trimmedName, entry)
    }))
    .filter((entry) => entry.score >= minScore && entry.mockupUrl)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aTime = a.updatedAt?.toMillis?.() ?? a.updatedAtMs ?? 0;
      const bTime = b.updatedAt?.toMillis?.() ?? b.updatedAtMs ?? 0;
      return bTime - aTime;
    });

  return scored.slice(0, limit);
}

/**
 * Register or update the official mockup for a customer + design name.
 * Most recent call wins (overwrites the index entry for that normalized name).
 */
export async function   upsertCustomerDesignMockup({
  customerId,
  designName,
  mockupUrl,
  mockupFileName = null,
  sourceLineId = null,
  sourceOrderId = null,
  sourceOrderNumber = null,
  lineSnapshot = null
}) {
  const persistableUrl = getPersistableMockupUrl(mockupUrl);
  if (!customerId || !designName?.trim() || !persistableUrl) return null;

  const docId = buildDesignIndexDocId(customerId, designName);
  if (!docId) return null;

  const normalizedKey = normalizeDesignName(designName).replace(/\s+/g, '_');

  await setDoc(
    doc(db, CUSTOMER_DESIGN_MOCKUPS_COLLECTION, docId),
    {
      customerId,
      normalizedKey,
      displayName: designName.trim(),
      mockupUrl: persistableUrl,
      mockupFileName,
      sourceLineId,
      sourceOrderId,
      sourceOrderNumber,
      ...(lineSnapshot ? { lineSnapshot } : {}),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return docId;
}

/**
 * Register a mockup from a line item if one exists.
 */
export async function registerDesignMockupFromLine({
  customerId,
  line,
  orderId = null,
  orderNumber = null
}) {
  if (!customerId || !line) return null;

  const designName = line.design || line.designName;
  const mockupUrl = getMockupUrl(line);
  if (!designName?.trim() || !mockupUrl) return null;

  return upsertCustomerDesignMockup({
    customerId,
    designName: designName.trim(),
    mockupUrl,
    mockupFileName: line.files?.mockup?.fileName || null,
    sourceLineId: line.docId || line.id || null,
    sourceOrderId: orderId,
    sourceOrderNumber: orderNumber,
    lineSnapshot: buildLineSnapshotFromLine(line)
  });
}
